import { FC, useContext, useEffect, useState } from "react";
import { useRouter } from "next/router";

import { WIDGETS, WidgetsContext } from "@/components/shared/widgets";
import Router from "@/helpers/router";
import { useDialerGlobalContext } from "@/hooks/dialer/use-dialer-global-context";
import { useGlobalContext } from "@/hooks/use-global-context";
import { useWhatsNewOpenState } from "@/modules/whats-new/lib/open-state";

import { useFetchDailyTodos } from "../data/queries";
import { useDailyTodosViewer } from "../data/use-daily-todos-viewer";
import { GATE_LAYERS, getGateLayer } from "../lib/schedule";
import { DailyTodosGateDialog } from "./gate-dialog";

/** How often the gate rechecks the clock (reminder time, snooze end). */
const TICK_MS = 30_000;

/** Widgets that mean a call may be live: the list dialer and the account panel. */
const BUSY_WIDGETS: Array<string> = [WIDGETS.DIALER, WIDGETS.MAXED_DIALER];

/**
 * The daily To-Dos gate (C-16 S6, C16-D474), mounted once in AppLayout:
 * Command Center's welcome popup, tutorial, time ping, snooze and "finish
 * first" lock. Callers and Pod Leads only, never while impersonating, never
 * on top of "What's new", on the To-Dos page, in the dialer, while dialing or
 * while an account panel is open (a call may be live there). Off (404 +
 * 1035), failing (a refetch error too) or not a caller, it shows nothing.
 * Times are the server's (the response's `now` against this browser's clock).
 */
export const DailyTodosGate: FC = () => {
  const router = useRouter();
  const { glencocoUser } = useGlobalContext();
  const { isCaller, isImpersonated } = useDailyTodosViewer();
  const { data, isError } = useFetchDailyTodos(isCaller);
  const { isEnabled: isDialing } = useDialerGlobalContext();
  const { activeWidgets } = useContext(WidgetsContext);
  const whatsNew = useWhatsNewOpenState();
  const [clock, setClock] = useState(() => Date.now());
  const [isBriefingClosed, setIsBriefingClosed] = useState(false);
  const [isIntroDeferred, setIsIntroDeferred] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setClock(Date.now()), TICK_MS);

    return () => clearInterval(timer);
  }, []);

  const now = new Date(clock + (data?.clockOffsetMs ?? 0));
  const isBusy =
    !!isDialing ||
    !!activeWidgets?.some((widget) => BUSY_WIDGETS.includes(widget?.id));

  const layer =
    !isCaller ||
    isImpersonated ||
    isError ||
    !whatsNew.hasDecided ||
    whatsNew.isOpen
      ? GATE_LAYERS.NONE
      : getGateLayer({
          todos: data?.todos ?? null,
          now,
          pathname: router.pathname,
          isBusy,
          isBriefingClosed,
          isIntroDeferred,
        });

  return (
    <DailyTodosGateDialog
      layer={layer}
      todos={data?.todos ?? null}
      firstName={glencocoUser?.first_name?.trim() || undefined}
      now={now}
      onGo={() => void router.push(Router.todos())}
      onBriefingLater={() => setIsBriefingClosed(true)}
      onIntroLater={() => setIsIntroDeferred(true)}
    />
  );
};
